import { FormControl, FormGroup } from '@angular/forms';
import type {
  IAddress,
  ICodeableConcept,
  IContact,
  IContactPoint,
  IHumanName,
  IIdentification,
  IdentificationUse,
  ContactPointSystem,
  ContactPointUse,
  AddressUse,
  AddressType,
  IAppointmentStatus,
  ICodeableConceptModel
} from './hl7-contracts';
import { jwtDecode } from 'jwt-decode';

interface CalculationType {
  _id: string;
  symbol: string;
  decimal?: boolean;
}

interface CurrencyFormat {
  decimalSeparator: string;
  thousandsSeparator: string;
  padRight: number;
}

export class CodeableConcept implements Partial<ICodeableConceptModel> {
  get active() {
    return this.active;
  }
  set active(value: boolean) {
    this.active = value;
  }
  get displays() {
    return this.displays;
  }
  set displays(value: any) {
    this.displays = value;
  }
}
export class Utils {
  static setChangeStatus(state: any, user?: string, confirm?: any, motive?: string) {
    const object = {};
    const status = object as IAppointmentStatus;
    status.statusType = confirm;
    status.confirmationMethod = state;
    status.date = new Date();
    status.user = user;
    status.reasonSuspended = motive;
    return status;
  }

  static downloadStatus(res, filename) {
    const navigatorWithLegacySave = window.navigator as Navigator & {
      msSaveBlob?: (blob: Blob, defaultName?: string) => boolean;
      msSaveOrOpenBlob?: (blob: Blob, defaultName?: string) => boolean;
    };

    if (navigatorWithLegacySave.msSaveOrOpenBlob) {
      // IE specific download.
      navigatorWithLegacySave.msSaveBlob(res, filename);
    } else {
      const downloadLink = document.createElement('a');
      downloadLink.style.display = 'none';
      document.body.appendChild(downloadLink);
      downloadLink.setAttribute('href', window.URL.createObjectURL(res));
      downloadLink.setAttribute('download', filename);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    }
  }

  static validateAllFormFields(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach(field => {
      const control = formGroup.get(field);
      if (control instanceof FormControl) {
        control.markAsTouched({ onlySelf: true });
      } else if (control instanceof FormGroup) {
        this.validateAllFormFields(control);
      }
    });
  }

  static getCodeableConceptValue(codeableConcept: ICodeableConcept, language: string) {
    return codeableConcept && codeableConcept.displays && codeableConcept.displays.find(item => item.language === language)
      ? codeableConcept.text
      : '';
  }

  static getCodeableConceptTranslationText(codeableConcept: ICodeableConcept, language: string): string {
    return codeableConcept.displays.find(item => item.language === language).value;
  }

  static getCodeableConceptTranslationAbbreviation(codeableConcept: ICodeableConcept, language: string): any {
    return codeableConcept.displays.find(item => item.language === language);
  }

  static getTranslate(data: any, language: string): string {
    return data.displays.find(item => item.language === language).value;
  }

  static setHumanNameNoArray(name: string, lastname?: string) {
    const object = {};
    const humanName = object as IHumanName;
    humanName.use = 'usual';
    humanName.text = name;
    humanName.family = lastname || '';
    return humanName;
  }

  static setHumanName(name: string, lastname?: string, prefix?: string[], suffix?: string[]): IHumanName[] {
    const object = {};
    const humanName = object as IHumanName;
    humanName.use = 'usual';
    humanName.text = name;
    humanName.family = lastname || '';
    humanName.prefix = prefix || [''];
    humanName.suffix = suffix || [''];
    return [humanName];
  }

  static setIdentifier(value: string, type: ICodeableConceptModel, use: IdentificationUse): IIdentification {
    if (value) {
      const object = {};
      const identifier = object as IIdentification;
      identifier.use = use;
      identifier.identifierType = type;
      identifier.value = value;
      return identifier;
    }
    return undefined;
  }

  static setFiscalIdentifier(value: string, type: ICodeableConceptModel): IIdentification {
    return this.setIdentifier(value, type, 'usual');
  }
  static setSecondary(value: string, type: ICodeableConceptModel): IIdentification {
    return this.setIdentifier(value, type, 'secondary');
  }

  static setOfficialIdentifier(value: string, type: ICodeableConceptModel): IIdentification {
    return this.setIdentifier(value, type, 'official');
  }

  static setRecordNumber(value: string, type: ICodeableConceptModel): IIdentification {
    return this.setIdentifier(value, type, 'usual');
  }

  static setCodeableConcept(codeableConcept: ICodeableConceptModel): ICodeableConceptModel {
    return codeableConcept;
  }

  static setContactPoint(value: string, system: ContactPointSystem, use: ContactPointUse): IContactPoint {
    const object = {};
    const telecom = object as IContactPoint;
    telecom.system = system;
    telecom.value = value;
    telecom.use = use;
    return telecom;
  }

  static setTelecomEmail(value: string): IContactPoint {
    return this.setContactPoint(value, 'email', undefined);
  }

  static setTelecomHomePhone(value: string): IContactPoint {
    return this.setContactPoint(value, 'phone', 'home');
  }

  static setTelecomWorkPhone(value: string): IContactPoint {
    return this.setContactPoint(value, 'phone', 'work');
  }

  static setTelecomMobilePhone(value: string): IContactPoint {
    return this.setContactPoint(value, 'phone', 'mobile');
  }

  static setAddressData(text: string, postalCode: string, _use: AddressUse, _type: AddressType, country?: ICodeableConceptModel): IAddress {
    const object = {};
    const address = object as IAddress;
    address.postalCode = postalCode;
    address.use = 'home';
    address.type = 'both';
    address.text = text;
    address.country = country;
    return address;
  }

  static setResidencialAddress(text: string, postalCode: string, country?: ICodeableConceptModel): IAddress {
    return this.setAddressData(text, postalCode, 'home', 'both', country);
  }

  static setWorklAddress(text: string, postalCode: string, country?: ICodeableConceptModel): IAddress {
    return this.setAddressData(text, postalCode, 'work', 'both', country);
  }

  static setContact(
    name: IHumanName,
    telecom: IContactPoint[],
    address: IAddress,
    identification: IIdentification,
    insuranceCompanies?: any,
    relationship?: ICodeableConceptModel,
    gender?: ICodeableConceptModel
  ): IContact {
    const object = {};
    const contact = object as IContact;

    contact.relationship = relationship;
    contact.identifier = identification ? [identification] : [undefined];
    contact.name = name;
    contact.telecom = telecom;
    contact.address = address;
    contact.gender = gender;
    contact.insuranceCompanies = insuranceCompanies;

    return contact;
  }

  static setInsuranceCompany(name: string, number: string) {
    return [
      {
        name,
        number
      }
    ];
  }

  static setCoding(value: any): any {
    return {
      system: value.system,
      version: value.version,
      code: value.code,
      display: '',
      userSelected: true
    };
  }

  static calculateAge(dateIn: Date) {
    const parameter = new Date(dateIn);
    const timeDif = Date.now() - parameter.getTime();
    const ageDate = new Date(timeDif);
    const year = Math.abs(ageDate.getUTCFullYear() - 1970);
    const month = ageDate.getUTCMonth();
    const days = ageDate.getUTCDate();

    const data: any = {
      year: '',
      month: '',
      day: ''
    };

    if (year < 2) {
      data.year = year;
      data.month = month;
      data.day = days;
      return data;
    } else if (year >= 2 && year < 12) {
      data.year = year;
      data.month = month;
      return data;
    } else if (year >= 12) {
      data.year = year;
      return data;
    } else {
      return '';
    }
  }
  // this function only allows entering text without numbers and special characters
  static validatorOnlyLetters(event): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode > 32 && (charCode < 65 || charCode > 90) && (charCode < 97 || charCode > 122) && (charCode < 209 || charCode > 250)) {
      return false;
    }
    return true;
  }

  static patientCharsValidator(event): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    if (
      charCode !== 45 &&
      charCode > 32 &&
      (charCode < 65 || charCode > 90) &&
      (charCode < 97 || charCode > 122) &&
      (charCode < 209 || charCode > 250) &&
      charCode > 31 &&
      (charCode < 48 || charCode > 57)
    ) {
      return false;
    }
    return true;
  }

  static validatorOnlyNumbers(event): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
      return false;
    }
    return true;
  }
  static validatorOnlyNumbersAndPlus(event): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode > 31 && charCode !== 43 && (charCode < 48 || charCode > 57)) {
      return false;
    }

    if (charCode === 43) {
      return true;
    }
    if (charCode === 101) {
      return false;
    }
    return true;
  }
  static validatorOnlyDate(event): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode > 31 && (charCode < 47 || charCode > 57)) {
      return false;
    }
    return true;
  }
  // This function validates when text is pasted into the input field, converts the string to an array and validates
  // it with the ValidatorOnlyLetters function and returns a response if it is false
  static validatePaste(event) {
    let valid: boolean;
    const text = event.target.value;
    const charactersArray = text.split('');
    for (const c of charactersArray) {
      valid = this.validatorOnlyLetters({ which: c.charCodeAt(0) });
      if (valid) {
      } else {
        return valid;
      }
    }
  }

  static sortZToA(item, value) {
    item.sort((a, b) => {
      if (a[value] < b[value]) {
        return 1;
      }
      if (a[value] > b[value]) {
        return -1;
      }
      return 0;
    });
    return item;
  }
  static sortAToZ(item, value) {
    item.sort((a, b) => {
      if (a[value] && b[value]) {
        return a[value].localeCompare(b[value]);
      }
    });
    return item;
  }

  traslateErrors(_text: String) {}

  static convertStartTimeNum(startTime) {
    const hour = startTime.getHours();
    const minute = this.addZero(startTime.getMinutes());
    const timeStart = hour + '.' + minute;
    const timeOne = Number((Number(timeStart) * 60).toFixed(2));
    return timeOne;
  }

  static convertEndTimeNum(endTime) {
    const hourEnd = endTime.getHours();
    const minuteEnd = this.addZero(endTime.getMinutes());
    const timeEnd = hourEnd + '.' + minuteEnd;
    const timeTwo = Number((Number(timeEnd) * 60).toFixed(2));
    return timeTwo;
  }

  static takeToStartTime(startTime, date?) {
    const h1 = (startTime / 60).toFixed(2);
    let dateInitial;
    date ? (dateInitial = new Date(date)) : (dateInitial = new Date());
    dateInitial.setHours(parseInt(h1.split('.')[0]));
    dateInitial.setMinutes(parseInt(h1.split('.')[1]));
    dateInitial.setSeconds(0);
    dateInitial.setMilliseconds(0);
    return dateInitial;
  }

  static takeToEndTime(endTime, date?) {
    const h2 = (endTime / 60).toFixed(2);
    let dateEnd;
    date ? (dateEnd = new Date(date)) : (dateEnd = new Date());
    dateEnd.setHours(parseInt(h2.split('.')[0]));
    dateEnd.setMinutes(parseInt(h2.split('.')[1]));
    return dateEnd;
  }

  static addZero(i) {
    if (i < 10) {
      i = '0' + i;
    }
    return i;
  }

  static parsePipeInput(input: string): string {
    return input
      .toLowerCase()
      .replace(/á/g, 'a')
      .replace(/é/g, 'e')
      .replace(/í/g, 'i')
      .replace(/ó/g, 'o')
      .replace(/ú/g, 'u');
  }

  static calculateVitalSign(
    type: any,
    index: number,
    vitalSignsTypes: CalculationType[],
    vitalSignsList: any[],
    currencySettings: CurrencyFormat
  ): string {
    const formula: string = type.equation;
    const regExp: RegExp = /\[(.+?)\]/g;
    const symbols: any[] = formula.match(regExp);
    let preFormula: string = formula;
    const calculationVariables: any[] = [];
    const contentDecimal = vitalSignsTypes.some(vs => vs.decimal);

    // validate symbols exist
    for (const symbol of symbols) {
      for (const vitalSignType of vitalSignsTypes) {
        // Clear vars removing [] for get simbol
        if (symbol.replace(/[\[\]']+/g, '') === vitalSignType.symbol) {
          calculationVariables.push(vitalSignType);
        }
      }
    }

    // Get calculate vars
    const varsToCalculate = vitalSignsList[index].value.filter(vs => calculationVariables.some(cv => cv._id === vs.vitalSignType._id));

    varsToCalculate.forEach(
      vc =>
        (preFormula = preFormula
          // Replace var name with var value in formula
          .replace(new RegExp(`\\[${vc.vitalSignType.symbol}?\]\s?`, 'g'), vc.value))
    );
    preFormula = preFormula.replace(/ /g, '');
    // Evaluate formula
    try {
      //  const result = evaluate(preFormula);
      const result = Function('return ' + preFormula)();
      return contentDecimal ? this.formattedCurrency(result, currencySettings) : result;
    } catch (e) {
      return 'N/A';
    }
  }

  static calculateHydricBalance(
    type: any,
    index: number,
    hydricBalanceTypes: CalculationType[],
    hydricBalanceList: any[],
    currencySettings: CurrencyFormat
  ): string {
    const formula: string = type.equation;
    const regExp: RegExp = /\[(.+?)\]/g;
    const symbols: any[] = formula.match(regExp);
    let preFormula: string = formula;
    const calculationVariables: any[] = [];
    const contentDecimal = hydricBalanceTypes.some(hb => hb.decimal);

    // validate symbols exist
    for (const symbol of symbols) {
      for (const hydricBalanceType of hydricBalanceTypes) {
        // Clear vars removing [] for get simbol
        if (symbol.replace(/[\[\]']+/g, '') === hydricBalanceType.symbol) {
          calculationVariables.push(hydricBalanceType);
        }
      }
    }

    // Get calculate vars
    const varsToCalculate = hydricBalanceList[index].value.filter(hb => calculationVariables.some(cv => cv._id === hb.hydricBalanceType._id));

    varsToCalculate.forEach(
      vc =>
        (preFormula = preFormula
          // Replace var name with var value in formula
          .replace(new RegExp(`\\[${vc.hydricBalanceType.symbol}?\]\s?`, 'g'), vc.value))
    );
    preFormula = preFormula.replace(/ /g, '');
    // Evaluate formula
    try {
      //  const result = evaluate(preFormula);
      const result = Function('return ' + preFormula)();
      return contentDecimal ? this.formattedCurrency(result, currencySettings) : result;
    } catch (e) {
      return 'N/A';
    }
  }

  static calculateBodyMeasurement(
    type: any,
    index: number,
    bodyMeasurementTypes: CalculationType[],
    bodyMeasurementList: any[],
    currencySettings: CurrencyFormat
  ): string {
    const formula: string = type.equation;
    const regExp: RegExp = /\[(.+?)\]/g;
    const symbols: any[] = formula.match(regExp);
    let preFormula: string = formula;
    const calculationVariables: any[] = [];
    const contentDecimal = bodyMeasurementTypes.some(bm => bm.decimal);

    // validate symbols exist
    for (const symbol of symbols) {
      for (const bodyMeasurementType of bodyMeasurementTypes) {
        // Clear vars removing [] for get simbol
        if (symbol.replace(/[\[\]']+/g, '') === bodyMeasurementType.symbol) {
          calculationVariables.push(bodyMeasurementType);
        }
      }
    }

    // Get calculate vars
    const varsToCalculate = bodyMeasurementList[index].value.filter(bm => calculationVariables.some(cv => cv._id === bm.bodyMeasurementType._id));

    varsToCalculate.forEach(
      vc =>
        (preFormula = preFormula
          // Replace var name with var value in formula
          .replace(new RegExp(`\\[${vc.bodyMeasurementType.symbol}?\]\s?`, 'g'), vc.value))
    );
    preFormula = preFormula.replace(/ /g, '');
    // Evaluate formula
    try {
      //  const result = evaluate(preFormula);
      const result = Function('return ' + preFormula)();
      return contentDecimal ? this.formattedCurrency(result, currencySettings) : result;
    } catch (e) {
      return 'N/A';
    }
  }

  static countDecimals(value: any): any {
    if (!Number(value)) {
      return 2;
    }
    if (Math.floor(value) === value) {
      return 2;
    }
    const numberOfZero = value.toString().split('.')[1].length;
    return numberOfZero <= 1 ? 2 : numberOfZero;
  }

  static formattedCurrency(value: any, currencyFormat: CurrencyFormat) {
    if (currencyFormat) {
      const numberFormateD = parseFloat(value);
      const countDecimal = this.countDecimals(numberFormateD);
      const precision = countDecimal > currencyFormat.padRight ? currencyFormat.padRight : countDecimal;

      return this.formatNumber(Number(numberFormateD), precision, currencyFormat.decimalSeparator, currencyFormat.thousandsSeparator);
    }
  }

  private static formatNumber(value: number, precision: number, decimalSeparator: string, thousandsSeparator: string): string {
    const fixedValue = value.toFixed(precision);
    const isNegative = fixedValue.startsWith('-');
    const [integerPart, decimalPart] = fixedValue.replace('-', '').split('.');
    const groupedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);

    return `${isNegative ? '-' : ''}${groupedInteger}${precision > 0 ? `${decimalSeparator}${decimalPart}` : ''}`;
  }

  static unformattedCurrency(value: any, currencyFormat: CurrencyFormat) {
    if (currencyFormat && value) {
      const _value = value ? value.toString() : '';

      let output = _value.replace(/[$€ ]/g, '');
      const haveDot = output.indexOf('.') >= 0;
      const haveComma = output.indexOf(',') >= 0;
      if (currencyFormat.decimalSeparator === ',') {
        if (haveDot) {
          if (!Number(output)) {
            output = output.replace(/[.]/g, '');
            if (haveComma) {
              output = output.replace(/[,]/g, '.');
            }
          } else {
            if (typeof value === 'string') {
              output = output.replace(/[.]/g, '');
            }
          }
        } else if (haveComma) {
          output = output.replace(/[,]/g, '.');
        }
      }
      if (currencyFormat.decimalSeparator === '.') {
        if (haveComma) {
          output = output.replace(/[,]/g, '');
        }
      }
      return output;
    }
  }

  static addDaysToDate(date: Date, days: number): Date {
    const result = new Date(date);
    result.setDate(result.getDate() + days);

    return result;
  }

  static getDaysBetweenTwoDates(firstDate: Date, secondDate: Date): Number {
    if (!firstDate || !secondDate) {
      return 10;
    }

    const millisecondsPerDay = 1000 * 60 * 60 * 24;
    return Math.trunc((new Date(secondDate).getTime() - new Date(firstDate).getTime()) / millisecondsPerDay);
  }

  static scapeRegexString(text: string) {
    return text.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
  }

  static getRequiredPdfParams() {
    const userData = jwtDecode<{ practitioner: string }>(JSON.parse(localStorage.getItem('user')).token);
    return {
      config: userData.practitioner,
      lang: localStorage.getItem('lang')
    };
  }
  static getPdfFormats(extended?: boolean): any[] {
    return extended ? ['letter', 'legal', 'A4', 'folio', 'custom', 'mid-card'] : ['letter', 'legal', 'A4', 'folio', 'custom'];
  }

  static parsePDFDate(dateStr): string {
    const date = typeof dateStr === 'string' ? new Date(dateStr) : dateStr;
    const day = date.getUTCDate();
    const month = date.getUTCMonth() + 1;
    const year = date.getUTCFullYear();

    if (month < 10) {
      return `${day}0${month}${year}`;
    } else {
      return `${day}${month}${year}`;
    }
  }

  static randomGeoZone(lng: number, lat: number, radius: number = 2000) {
    const rd = radius / 111300;
    const w = rd * Math.sqrt(Math.random());
    const t = 2 * Math.PI * Math.random();
    const x = w * Math.cos(t);
    const y = w * Math.sin(t);
    const xp = x / Math.cos(lat);
    return `${xp + lng}, ${y + lat}`;
  }

  static getTimeMask() {
    const amPm = { input: 'DD/MM/YYYY, h:mm a', list: 'dd/MM/yyyy, h:mm a' };
    const military = { input: 'DD/MM/YYYY, HH:mm', list: 'dd/MM/yyyy, HH:mm' };
    const timeMask = JSON.parse(localStorage.getItem('GlobalSettings'));
    return timeMask.dateTime.timeMask === 'am-pm' ? amPm : military;
  }

  static getPractitionerProfileName(practitionerProfile): string {
    if (practitionerProfile.name && practitionerProfile.name.length) {
      const name = practitionerProfile.name[0];
      return `${name.prefix && name.prefix.length ? name.prefix[0] : ''} ${name.text} ${name.family} ${
        name.suffix && name.suffix.length ? name.suffix[0] : ''
      }`;
    }
    return '';
  }
  static getPatientHumanName(patient): string {
    if (patient.name && patient.name.length) {
      const name = patient.name[0];
      return `${name.text} ${name.family}`;
    }
    return '';
  }

}
